import {
  BuildingOffice,
  ChatCircle,
  CoinVertical,
  CreditCard,
  House,
  Info,
  MapPin,
  Question,
  SealCheck,
  ShieldPlus,
  ShoppingCart,
  Smiley,
  Swap,
  UserCircle,
} from '@phosphor-icons/react/dist/ssr'
import { memo, lazy, Suspense, type ComponentType, type LazyExoticComponent } from 'react'

import type { IconProps } from '@phosphor-icons/react'

// Common CMS icons, statically imported so they tree-shake into the main bundle
// and render synchronously. Anything not listed here is loaded via the dynamic
// fallback below. Importing the '@phosphor-icons/react/dist/ssr' barrel directly
// (as DynamicIcon used to) pulled the entire ~4.8 MB icon set into one chunk that
// loaded on every page (DynamicIcon is in the header nav). Add frequently-used
// icons here to keep them off the fallback chunk.
const STATIC_ICONS: Record<string, ComponentType<IconProps>> = {
  BuildingOffice,
  ChatCircle,
  CoinVertical,
  CreditCard,
  House,
  Info,
  MapPin,
  Question,
  SealCheck,
  ShieldPlus,
  ShoppingCart,
  Smiley,
  Swap,
  UserCircle,
}

// Convert kebab-case to PascalCase for component names
const kebabToPascal = (str: string): string => {
  return str
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('')
}

const createFallback = (iconName: string): ComponentType<IconProps> => {
  const Fallback = (props: IconProps) => (
    <div
      style={{
        width: props.size || 24,
        height: props.size || 24,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f3f4f6',
        borderRadius: '4px',
        fontSize: '12px',
        color: '#6b7280',
      }}
      title={`Icon "${iconName}" not found`}
    >
      ?
    </div>
  )

  return Fallback
}

// Cache for lazy-loaded (non-common) icons
const iconCache = new Map<string, LazyExoticComponent<ComponentType<IconProps>>>()

const getLazyIcon = (componentName: string, iconName: string) => {
  if (!iconCache.has(iconName)) {
    const LazyIcon = lazy(async () => {
      try {
        // A plain (non-pattern) dynamic import of the barrel resolves in both
        // webpack and Turbopack and is code-split into its own chunk, so the full
        // icon set only loads when an uncommon icon is actually rendered.
        const iconModule = await import('@phosphor-icons/react/dist/ssr')
        const IconComponent = iconModule[componentName as keyof typeof iconModule] as ComponentType<IconProps> | undefined

        return { default: IconComponent ?? createFallback(iconName) }
      } catch {
        return { default: createFallback(iconName) }
      }
    })

    iconCache.set(iconName, LazyIcon)
  }

  return iconCache.get(iconName)!
}

interface DynamicIconProps extends Omit<IconProps, 'children' | 'onError'> {
  name: string // Accept any string from CMS
  fallback?: React.ReactNode
  onError?: (iconName: string) => void
}

/**
 * Renders a Phosphor icon by its (kebab-case) name from the CMS.
 *
 * Common icons are bundled statically and render synchronously; any other icon is
 * lazy-loaded on demand so the full icon set never ships on the critical path.
 */
const DynamicIcon: React.FC<DynamicIconProps> = memo(({ name, fallback = <div style={{ width: 24, height: 24 }} />, onError, ...iconProps }) => {
  // Clean the icon name (remove special characters, normalize)
  const cleanName = name.toLowerCase().trim()

  if (!cleanName) {
    onError?.('empty-name')

    return <>{fallback}</>
  }

  const componentName = kebabToPascal(cleanName)

  // Fast path: common icons render synchronously from the static map.
  const StaticIcon = STATIC_ICONS[componentName]
  if (StaticIcon) {
    return <StaticIcon {...iconProps} />
  }

  const IconComponent = getLazyIcon(componentName, cleanName)

  return (
    <Suspense fallback={fallback}>
      <IconComponent {...iconProps} />
    </Suspense>
  )
})

DynamicIcon.displayName = 'DynamicIcon'

export default DynamicIcon
